<template>
  <div class="my-seat">
    <h1 class="my-seat__title">버스 좌석 지정</h1>

    <!-- 불러오는 중 -->
    <div v-if="phase === 'loading'" class="my-seat__box my-seat__box--center">
      <p class="my-seat__muted">좌석 정보를 불러오고 있어요…</p>
    </div>

    <!-- 안내 메시지 (대상 아님 / 오픈 전 / 마감 등) -->
    <div v-else-if="phase === 'message'" class="my-seat__box my-seat__box--center">
      <p class="my-seat__msg-title">{{ msgTitle }}</p>
      <p class="my-seat__msg-text">{{ msgText }}</p>
      <div class="my-seat__msg-actions">
        <button v-if="msgRetry" type="button" class="my-seat__btn my-seat__btn--primary" @click="start">다시 시도</button>
        <button type="button" class="my-seat__btn" @click="goBack">예약 상세로</button>
      </div>
    </div>

    <template v-else>
      <!-- 운행 정보 -->
      <section v-if="info" class="my-seat__box my-seat__info">
        <p class="my-seat__route">{{ info.routeName }}</p>
        <dl class="my-seat__meta">
          <div><dt>출발일</dt><dd>{{ fmtDay(info.roundDate) }}</dd></div>
          <div>
            <dt>탑승 버스</dt>
            <dd>{{ info.busSeq }}호차<span v-if="info.departTime"> · {{ info.departTime }} 출발</span></dd>
          </div>
          <div v-if="info.meetingPointName || info.meetingAddress">
            <dt>집결지</dt>
            <dd>{{ info.meetingPointName }}<small v-if="info.meetingAddress">{{ info.meetingAddress }}</small></dd>
          </div>
          <div><dt>지정 마감</dt><dd>{{ fmtDateTime(closeAt || info.seatCloseAt) }}</dd></div>
        </dl>
      </section>

      <!-- 지정 완료 -->
      <section v-if="phase === 'done'" class="my-seat__box my-seat__done">
        <p class="my-seat__done-title">좌석지정이 완료되었습니다</p>
        <div class="my-seat__chips">
          <span v-for="s in ownedSeats" :key="s.seatNo" class="my-seat__chip">{{ s.seatNo }}</span>
        </div>
        <p class="my-seat__muted">저장한 좌석은 직접변경이 불가합니다. 변경이 필요하면 고객센터(1660-4602)로 문의해 주십시오.</p>
      </section>

      <!-- 선택 안내 -->
      <section v-if="phase === 'select'" class="my-seat__guide">
        <p v-if="seatCnt <= 1">내 좌석 1석을 선택하여 주십시오.</p>
        <p v-else>예약 인원 {{ seatCnt }}명의 좌석을 선택하여 주십시오. 동행분의 좌석도 선택가능합니다.</p>
        <p v-if="ownedSeats.length" class="my-seat__muted">
          미리 배정된 좌석 {{ ownedSeats.map((s) => s.seatNo).join(', ') }}을 빼고 {{ needCnt }}석을 선택하시면 됩니다.
        </p>
      </section>

      <!-- 좌석맵 -->
      <section class="my-seat__map-wrap">
        <div class="my-seat__bus">
          <div class="my-seat__front">
            <span class="my-seat__driver">운전석</span>
            <span class="my-seat__door">출입문</span>
          </div>
          <div v-for="(row, rIdx) in seatRows" :key="rIdx" class="my-seat__row">
            <template v-for="seat in row" :key="seat.seatNo">
              <button
                type="button"
                class="my-seat__seat"
                :class="['is-' + seat.seatState, { 'is-busy': busySeat === seat.seatNo }]"
                :disabled="phase !== 'select'"
                :aria-label="seatLabel(seat)"
                @click="clickSeat(seat)"
              >
                <span v-if="seat.seatState === 'blocked'">×</span>
                <span v-else>{{ seat.seatNo }}</span>
              </button>
              <span v-if="seat.aisleAfter === 'Y'" class="my-seat__aisle" />
            </template>
          </div>
        </div>

        <ul class="my-seat__legend">
          <li><i class="is-avail" />선택 가능</li>
          <li v-if="phase === 'select'"><i class="is-mine" />내가 고른 좌석</li>
          <li><i class="is-owned" />내 좌석</li>
          <li><i class="is-taken" />선택 완료된 좌석</li>
          <li><i class="is-blocked" />지정 불가</li>
          <li v-if="hasFeeSeat"><i class="is-fee" />추가요금 좌석</li>
        </ul>
      </section>

      <ul v-if="phase === 'select'" class="my-seat__notes">
        <li>좌석을 고른 뒤 <b>{{ remainLimitText }}</b> 안에 저장하여 주십시오. 지정된 시간이 초과되면 선택이 해제됩니다.</li>
        <li>고른 좌석을 다시 누르면 선택이 최소됩니다.</li>
        <li>저장한 뒤에는 직접 변경은 불가합니다, 변경은 고객센터(1660-4602)로 문의하여 주십시오.</li>
      </ul>

      <div v-if="phase === 'done'" class="my-seat__bottom-actions">
        <button type="button" class="my-seat__btn" @click="goBack">예약 상세로</button>
      </div>

      <!-- 하단 고정 바 -->
      <div v-if="phase === 'select'" class="my-seat__bar">
        <div class="my-seat__bar-info">
          <span class="my-seat__timer" :class="{ 'is-warn': remain <= 60 }">{{ remainText }}</span>
          <span class="my-seat__count"><b>{{ mineSeats.length }}</b> / {{ needCnt }}석</span>
        </div>
        <div class="my-seat__bar-actions">
          <button type="button" class="my-seat__btn my-seat__btn--ghost" :disabled="saving" @click="cancelAndLeave">나가기</button>
          <button type="button" class="my-seat__btn my-seat__btn--primary" :disabled="!canSave || saving" @click="save">
            {{ saving ? '저장 중…' : '좌석 저장' }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
// 좌석지정은 예약 상세(ReserveInfoDetail_client)에서 들어와요 → 나가면 그 예약 상세로 돌아가요
const DETAIL_PATH = '/reserveInfoDetail_client';
const MYPAGE_PATH = '/myPage';

// 세션 남은시간·좌석 상태를 서버와 맞추는 주기 (ms)
const POLL_MS = 15000;

// CALL 응답은 드라이버에 따라 [{...}] 또는 [[{...}], OkPacket] 형태라서 둘 다 처리
function firstRow(res) {
  const first = Array.isArray(res) ? res[0] : null;
  return Array.isArray(first) ? first[0] : first;
}

// 좌석지정 시작 결과별 안내
const START_MSG = {
  FORBIDDEN: ['예약을 확인할 수 없어요', '로그인한 계정의 예약이 아니거나 취소된 예약이에요.'],
  NOTFOUND: ['예약을 찾을 수 없어요', '예약번호를 다시 확인해주세요.'],
  NO_CHANNEL: ['좌석지정 대상이 아니에요', '좌석지정은 홈페이지에서 예약한 멤버십 회원만 할 수 있어요. 좌석은 출발 당일 현장에서 안내해드려요.'],
  NO_MEMBER: ['멤버십 회원 전용 서비스예요', '좌석지정은 예약하신 분이 멤버십 회원일 때 이용할 수 있어요. 좌석은 출발 당일 현장에서 안내해드려요.'],
  MEM_EXPIRE: ['멤버십 기간을 확인해주세요', '멤버십이 출발일까지 유지되어야 좌석을 지정할 수 있어요.'],
  NOBUS: ['버스 배정이 아직 끝나지 않았어요', '잠시 후 다시 시도해주세요. 계속 같은 안내가 나오면 고객센터로 문의해주세요.'],
  NOTOPEN: ['아직 좌석지정 기간이 아니에요', '출발 2일 전에 알림톡과 앱 알림으로 좌석지정 안내를 보내드려요. 안내를 받은 뒤 다시 들어와주세요.'],
  CLOSED: ['좌석지정이 마감되었어요', '좌석지정은 출발 전날 13:00에 마감돼요. 좌석 변경은 고객센터로 문의해주세요.'],
  FULL: ['지금 고를 수 있는 좌석이 부족해요', '고객센터로 문의해주시면 좌석을 안내해드릴게요.'],
};
// 다시 시도 버튼을 보여줄 결과 (시간이 지나면 풀릴 수 있는 것)
const RETRY_CODES = ['NOBUS', 'NOTOPEN'];

const HOLD_MSG = {
  TAKEN: '방금 다른 분이 선택한 좌석이에요. 다른 좌석을 골라주세요.',
  BLOCKED: '선택할 수 없는 좌석이에요.',
  FEE: '추가요금 좌석은 고객센터에서 안내해드려요.',
  NOSEAT: '좌석 정보를 찾을 수 없어요. 화면을 새로 불러올게요.',
  MAXSEAT: '예약 인원만큼 좌석을 모두 골랐어요.',
};

export default {
  name: 'MyBusSeat',

  data() {
    return {
      resCode: '',
      pCode: '', // 예약 상세로 돌아갈 때 쓰는 상품코드
      phase: 'loading', // loading | select | done | message
      msgTitle: '',
      msgText: '',
      msgRetry: false,

      sessionId: '',
      busId: null,
      seatCnt: 0,
      needCnt: 0,
      closeAt: null,
      info: null,
      seats: [],

      deadline: 0, // 세션 만료 시각 (이 기기 시계 기준 ms)
      remain: 0,
      tickTimer: null,
      pollTimer: null,
      lostShown: false,

      busySeat: '',
      saving: false,
      leaveOk: false, // 나가기 확인을 이미 받은 경우
    };
  },

  computed: {
    user() {
      return this.$store.state.user || {};
    },
    // 고객 로그인 계정 (= tb_reserve_m.reqEmail) - MyPage 와 같은 기준
    userEmail() {
      return this.user.email || '';
    },
    seatRows() {
      const rows = [];
      this.seats.forEach((s) => {
        const rIdx = s.seatRow - 1;
        if (!rows[rIdx]) rows[rIdx] = [];
        rows[rIdx][s.seatOrder - 1] = s;
      });
      return rows.filter(Boolean).map((r) => r.filter(Boolean));
    },
    mineSeats() {
      return this.seats.filter((s) => s.seatState === 'mine');
    },
    ownedSeats() {
      return this.seats.filter((s) => s.seatState === 'owned');
    },
    hasFeeSeat() {
      return this.seats.some((s) => s.seatState === 'fee');
    },
    canSave() {
      return this.needCnt > 0 && this.mineSeats.length === this.needCnt;
    },
    remainText() {
      const m = Math.floor(this.remain / 60);
      const s = this.remain % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    // 마감이 10분 안쪽이면 세션이 마감 시각에 끝나요
    remainLimitText() {
      return this.remain > 0 && this.remain < 600 ? `남은 시간(${this.remainText})` : '10분';
    },
  },

  created() {
    this.resCode = String(this.$route.query.resCode || '').trim();
    this.pCode = String(this.$route.query.pCode || '').trim();
    this.start();
  },

  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('visibilitychange', this.onVisible);
  },

  beforeUnmount() {
    this.stopTimers();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('visibilitychange', this.onVisible);
  },

  // 고른 좌석이 있는 채로 다른 화면으로 가면 확인 후 세션을 정리해서 좌석을 바로 풀어줘요
  async beforeRouteLeave(to, from, next) {
    if (this.phase !== 'select' || !this.sessionId || this.leaveOk) return next();
    if (this.mineSeats.length) {
      const ok = await this.confirmLeave();
      if (!ok) return next(false);
    }
    await this.cancelSession();
    next();
  },

  methods: {
    // ───────── 날짜 표시 ─────────
    parseDt(v) {
      if (!v) return null;
      const s = String(v);
      const d = s.endsWith('Z') ? new Date(s) : new Date(s.replace(' ', 'T'));
      return isNaN(d) ? null : d;
    },
    fmtDay(v) {
      const d = this.parseDt(v);
      if (!d) return v ? String(v).slice(0, 10) : '-';
      const week = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
      return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${week})`;
    },
    fmtDateTime(v) {
      const d = this.parseDt(v);
      if (!d) return '-';
      const week = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getMonth() + 1}월 ${d.getDate()}일 (${week}) ${p(d.getHours())}:${p(d.getMinutes())}`;
    },

    // ───────── 시작·상태 전환 ─────────
    async start() {
      this.stopTimers();
      this.lostShown = false;
      this.phase = 'loading';

      if (!this.userEmail) {
        // 비회원 접근 - MyPage 와 같은 로그인 화면으로
        this.$router.push({ path: '/b2CLogin', query: { st: 'Y' } });
        return;
      }
      if (!this.resCode) {
        this.showMessage('NOTFOUND');
        return;
      }

      try {
        const res = await this.$api('/api/proc_startBusSeatSession', { param: [this.resCode, this.userEmail] });
        const row = firstRow(res);
        if (!row) throw new Error('empty');

        if (row.result === 'OK') {
          await this.enterSelect(row);
        } else if (row.result === 'DONE') {
          await this.enterDone(row.busId);
        } else {
          this.showMessage(row.result);
        }
      } catch (e) {
        console.log('[MyBusSeat] start', e);
        this.msgTitle = '잠시 후 다시 시도해주세요';
        this.msgText = '좌석 정보를 불러오지 못했어요.';
        this.msgRetry = true;
        this.phase = 'message';
      }
    },

    showMessage(code) {
      const m = START_MSG[code] || ['좌석지정을 할 수 없어요', '고객센터로 문의해주세요.'];
      this.msgTitle = m[0];
      this.msgText = m[1];
      this.msgRetry = RETRY_CODES.includes(code);
      this.sessionId = '';
      this.phase = 'message';
    },

    async enterSelect(row) {
      this.sessionId = row.sessionId;
      this.busId = row.busId;
      this.seatCnt = Number(row.seatCnt) || 0;
      this.needCnt = Number(row.needCnt) || 0;
      this.closeAt = row.closeAt;
      this.setRemain(row.remainSec);
      await Promise.all([this.loadInfo(), this.loadSeats()]);
      this.phase = 'select';
      this.startTimers();
    },

    async enterDone(busId) {
      this.stopTimers();
      this.sessionId = '';
      this.busId = busId;
      await Promise.all([this.loadInfo(), this.loadSeats()]);
      this.phase = 'done';
    },

    async loadInfo() {
      const res = await this.$api('/api/getBusInfo', { param: [this.busId] });
      this.info = (Array.isArray(res) && res[0]) || null;
    },

    async loadSeats() {
      const res = await this.$api('/api/getBusSeatMapForCustomer', {
        param: [this.sessionId || '', this.resCode, this.busId],
      });
      if (Array.isArray(res)) this.seats = res;
    },

    // ───────── 10분 타이머 ─────────
    setRemain(sec) {
      const n = Math.max(0, Number(sec) || 0);
      this.deadline = Date.now() + n * 1000;
      this.remain = n;
    },
    startTimers() {
      this.stopTimers();
      this.tickTimer = setInterval(this.tick, 1000);
      this.pollTimer = setInterval(this.poll, POLL_MS);
    },
    stopTimers() {
      if (this.tickTimer) clearInterval(this.tickTimer);
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.tickTimer = null;
      this.pollTimer = null;
    },
    tick() {
      this.remain = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
      if (this.remain === 0) this.onSessionLost();
    },
    // 서버 기준 남은 시간 + 다른 고객의 선택 상황을 함께 맞춰요
    async poll() {
      if (this.phase !== 'select' || !this.sessionId) return;
      try {
        const res = await this.$api('/api/getBusSeatSession', { param: [this.sessionId, this.resCode] });
        const row = Array.isArray(res) ? res[0] : null;
        if (!row) {
          this.onSessionLost();
          return;
        }
        this.setRemain(row.remainSec);
        await this.loadSeats();
      } catch (e) {
        console.log('[MyBusSeat] poll', e);
      }
    },
    // 모바일에서 다른 앱에 다녀오면 타이머가 늦게 돌 수 있어서 바로 맞춰요
    onVisible() {
      if (document.visibilityState === 'visible') this.poll();
    },

    // 세션이 끝남 (10분 경과 / 마감 / 관리자가 좌석을 정리한 경우)
    async onSessionLost() {
      if (this.lostShown || this.phase !== 'select') return;
      this.lostShown = true;
      this.stopTimers();
      this.sessionId = '';
      const r = await this.$swal.fire({
        title: '선택 시간이 끝났어요',
        text: '고른 좌석은 다른 분이 선택할 수 있게 풀렸어요. 다시 골라주세요.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: '다시 고르기',
        cancelButtonText: '예약 상세로',
        allowOutsideClick: false,
      });
      if (r.isConfirmed) this.start();
      else this.goBack();
    },

    // ───────── 좌석 선택 ─────────
    seatLabel(seat) {
      const state = {
        mine: '내가 고른 좌석',
        owned: '내 좌석',
        taken: '선택 완료된 좌석',
        blocked: '지정 불가 좌석',
        fee: '추가요금 좌석',
        avail: '선택 가능',
      }[seat.seatState];
      return `${seat.seatNo} ${state || ''}`;
    },

    toast(title, icon = 'info') {
      this.$swal.fire({ toast: true, position: 'top', timer: 2000, showConfirmButton: false, icon, title });
    },

    async clickSeat(seat) {
      if (this.phase !== 'select' || this.busySeat || this.saving) return;

      switch (seat.seatState) {
        case 'mine':
          await this.release(seat);
          return;
        case 'avail':
          if (this.mineSeats.length >= this.needCnt) {
            this.toast(`${this.needCnt}석을 모두 골랐어요. 바꾸려면 고른 좌석을 다시 눌러 취소해주세요.`);
            return;
          }
          await this.hold(seat);
          return;
        case 'owned':
          this.toast('이미 정해진 내 좌석이에요. 변경은 고객센터로 문의해주세요.');
          return;
        case 'taken':
          this.toast('다른 분이 선택한 좌석이에요.');
          return;
        case 'fee':
          this.toast(HOLD_MSG.FEE);
          return;
        default:
          this.toast(HOLD_MSG.BLOCKED);
      }
    },

    async hold(seat) {
      this.busySeat = seat.seatNo;
      try {
        const res = await this.$api('/api/proc_holdBusSeat', { param: [this.sessionId, this.resCode, seat.seatNo] });
        const row = firstRow(res);
        const result = row && row.result;
        if (result === 'OK') {
          seat.seatState = 'mine';
          return;
        }
        if (result === 'NOSESSION' || result === 'EXPIRED') {
          this.onSessionLost();
          return;
        }
        this.toast(HOLD_MSG[result] || '좌석을 선택하지 못했어요.', result === 'TAKEN' ? 'warning' : 'info');
        await this.loadSeats();
      } catch (e) {
        console.log('[MyBusSeat] hold', e);
        this.toast('좌석을 선택하지 못했어요. 다시 시도해주세요.', 'error');
      } finally {
        this.busySeat = '';
      }
    },

    async release(seat) {
      this.busySeat = seat.seatNo;
      try {
        const res = await this.$api('/api/proc_releaseBusSeatHold', { param: [this.sessionId, this.resCode, seat.seatNo] });
        const row = firstRow(res);
        if (row && row.result === 'NOSESSION') {
          this.onSessionLost();
          return;
        }
        seat.seatState = 'avail';
        if (!row || row.result !== 'OK') await this.loadSeats();
      } catch (e) {
        console.log('[MyBusSeat] release', e);
        this.toast('선택을 취소하지 못했어요. 다시 시도해주세요.', 'error');
      } finally {
        this.busySeat = '';
      }
    },

    // ───────── 저장 ─────────
    async save() {
      if (!this.canSave || this.saving) return;
      const all = [...this.ownedSeats, ...this.mineSeats].map((s) => s.seatNo).join(', ');
      const ok = await this.$swal.fire({
        title: '이 좌석으로 저장할까요?',
        html: `<b>${all}</b><br><br>저장한 뒤에는 직접 바꿀 수 없어요.<br>변경이 필요하면 고객센터로 문의해주세요.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '저장',
        cancelButtonText: '다시 볼게요',
      });
      if (!ok.isConfirmed) return;

      this.saving = true;
      try {
        const res = await this.$api('/api/proc_confirmBusSeats', { param: [this.sessionId, this.resCode] });
        const row = firstRow(res);
        const result = row && row.result;

        if (result === 'OK') {
          await this.enterDone(row.busId || this.busId);
          this.toast('좌석지정이 완료되었어요.', 'success');
          return;
        }
        if (result === 'NOSESSION' || result === 'EXPIRED') {
          this.onSessionLost();
          return;
        }
        if (result === 'COUNT') {
          await this.loadSeats();
          this.toast(`${row.needCnt}석을 모두 골라야 저장할 수 있어요.`, 'warning');
          return;
        }
        // CLOSED / 자격 변경(NO_MEMBER, MEM_EXPIRE 등) → 서버가 선택을 이미 풀었어요
        this.stopTimers();
        this.showMessage(result);
      } catch (e) {
        console.log('[MyBusSeat] save', e);
        this.toast('저장하지 못했어요. 다시 시도해주세요.', 'error');
      } finally {
        this.saving = false;
      }
    },

    // ───────── 나가기 ─────────
    async confirmLeave() {
      const r = await this.$swal.fire({
        title: '좌석지정을 그만둘까요?',
        text: '고른 좌석은 저장되지 않고, 다른 분이 선택할 수 있게 풀려요.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '나가기',
        cancelButtonText: '계속 고르기',
      });
      return r.isConfirmed;
    },

    async cancelSession() {
      if (!this.sessionId) return;
      const sid = this.sessionId;
      this.sessionId = '';
      this.stopTimers();
      try {
        await this.$api('/api/proc_cancelBusSeatSession', { param: [sid, this.resCode] });
      } catch (e) {
        console.log('[MyBusSeat] cancel', e);
      }
    },

    async cancelAndLeave() {
      if (this.mineSeats.length && !(await this.confirmLeave())) return;
      await this.cancelSession();
      this.leaveOk = true;
      this.goBack();
    },

    // 예약 상세로 (상품코드를 모르면 마이페이지로)
    goBack() {
      if (this.resCode && this.pCode) {
        this.$router.push({ path: DETAIL_PATH, query: { seqReq: this.resCode, pCode: this.pCode, cCode: 'Y' } });
      } else {
        this.$router.push({ path: MYPAGE_PATH });
      }
    },

    // 창을 닫거나 새로고침할 때 (고른 좌석은 남은 시간이 지나면 자동으로 풀려요)
    onBeforeUnload(e) {
      if (this.phase === 'select' && this.mineSeats.length) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
  },
};
</script>

<style scoped>
.my-seat {
  /* MyPage 색상과 맞춤 */
  --c-main: #5fa033;
  --c-main-soft: #f0fbe7;
  --c-mine: #e07b00;
  --c-line: #ddefd4;
  --c-text: #1e2e1e;
  --c-muted: #6b7c6b;

  font-family: 'Noto Sans KR', 'Apple SD Gothic Neo', sans-serif;
  max-width: 560px;
  margin: 0 auto;
  padding: 16px 16px 120px;
  color: var(--c-text);
  box-sizing: border-box;
}
.my-seat__title {
  font-size: 20px;
  font-weight: 700;
  margin: 4px 0 14px;
}
.my-seat__box {
  border: 1px solid var(--c-line);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
  background: #fff;
}
.my-seat__box--center {
  text-align: center;
  padding: 36px 16px;
}
.my-seat__muted {
  color: var(--c-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 6px 0 0;
}
.my-seat__msg-title {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 8px;
}
.my-seat__msg-text {
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin: 0;
  word-break: keep-all;
}
.my-seat__msg-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 20px;
}

/* 운행 정보 */
.my-seat__route {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 10px;
}
.my-seat__meta {
  margin: 0;
  display: grid;
  gap: 6px;
}
.my-seat__meta div {
  display: flex;
  gap: 10px;
  font-size: 14px;
}
.my-seat__meta dt {
  flex: 0 0 64px;
  color: var(--c-muted);
}
.my-seat__meta dd {
  margin: 0;
  word-break: keep-all;
}
.my-seat__meta dd small {
  display: block;
  color: var(--c-muted);
  font-size: 12px;
}

/* 완료 */
.my-seat__done {
  border-color: var(--c-main);
  background: var(--c-main-soft);
}
.my-seat__done-title {
  font-weight: 700;
  color: var(--c-main);
  margin: 0 0 10px;
}
.my-seat__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.my-seat__chip {
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--c-main);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
}

.my-seat__guide p {
  margin: 0 0 4px;
  font-size: 14px;
  line-height: 1.5;
  word-break: keep-all;
}
.my-seat__guide {
  margin-bottom: 12px;
}

/* 좌석맵 */
.my-seat__map-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.my-seat__bus {
  border: 2px solid #d5d5d5;
  border-radius: 28px 28px 14px 14px;
  padding: 14px 12px 12px;
  background: #fafafa;
  max-width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
}
.my-seat__front {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 11px;
  color: #999;
}
.my-seat__driver,
.my-seat__door {
  padding: 3px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
}
.my-seat__row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
}
.my-seat__aisle {
  width: 18px;
  flex: 0 0 18px;
}
.my-seat__seat {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border: 1px solid #c9c9c9;
  border-radius: 8px 8px 12px 12px;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  color: #444;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s, background 0.15s;
  -webkit-tap-highlight-color: transparent;
}
.my-seat__seat:disabled {
  cursor: default;
}
.my-seat__seat.is-avail:not(:disabled):active {
  transform: scale(0.94);
}
.my-seat__seat.is-mine {
  background: var(--c-mine);
  border-color: var(--c-mine);
  color: #fff;
}
.my-seat__seat.is-owned {
  background: var(--c-main);
  border-color: var(--c-main);
  color: #fff;
}
.my-seat__seat.is-taken {
  background: #d9d9d9;
  border-color: #d9d9d9;
  color: #9a9a9a;
  cursor: not-allowed;
}
.my-seat__seat.is-blocked {
  background: transparent;
  border-style: dashed;
  color: #bbb;
  cursor: not-allowed;
}
.my-seat__seat.is-fee {
  background: #fff7e0;
  border-color: #e9c46a;
  color: #b08400;
}
.my-seat__seat.is-busy {
  opacity: 0.5;
}

.my-seat__legend {
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 14px;
  font-size: 12px;
  color: #666;
}
.my-seat__legend li {
  display: flex;
  align-items: center;
  gap: 5px;
}
.my-seat__legend i {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid #c9c9c9;
  background: #fff;
}
.my-seat__legend i.is-mine {
  background: var(--c-mine);
  border-color: var(--c-mine);
}
.my-seat__legend i.is-owned {
  background: var(--c-main);
  border-color: var(--c-main);
}
.my-seat__legend i.is-taken {
  background: #d9d9d9;
  border-color: #d9d9d9;
}
.my-seat__legend i.is-blocked {
  background: transparent;
  border-style: dashed;
}
.my-seat__legend i.is-fee {
  background: #fff7e0;
  border-color: #e9c46a;
}

.my-seat__notes {
  margin: 18px 0 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.6;
  color: #666;
  word-break: keep-all;
}
.my-seat__bottom-actions {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* 버튼 */
.my-seat__btn {
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  border: 1px solid #ccc;
  background: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.my-seat__btn--primary {
  background: var(--c-main);
  border-color: var(--c-main);
  color: #fff;
}
.my-seat__btn--ghost {
  border-color: transparent;
  color: #666;
}
.my-seat__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* 하단 고정 바 */
.my-seat__bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid var(--c-line);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}
.my-seat__bar-info {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.my-seat__timer {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--c-main);
}
.my-seat__timer.is-warn {
  color: #d93a3a;
}
.my-seat__count {
  font-size: 13px;
  color: #666;
}
.my-seat__count b {
  color: var(--c-mine);
}
.my-seat__bar-actions {
  display: flex;
  gap: 6px;
}

@media (min-width: 480px) {
  .my-seat__seat {
    width: 50px;
    height: 50px;
    flex-basis: 50px;
    font-size: 13px;
  }
  .my-seat__aisle {
    width: 24px;
    flex-basis: 24px;
  }
  .my-seat__bar {
    left: 50%;
    right: auto;
    width: 560px;
    transform: translateX(-50%);
    border-radius: 14px 14px 0 0;
    border: 1px solid var(--c-line);
  }
}
</style>
